import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import AuthLayout from '../layout/AuthLayout';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, LogIn, Loader2, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { GoogleOAuthButton } from './GoogleOAuthButton';

interface LoginCredentials {
  email: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { clearClientState } = useClient();

  // Get success message from location state (e.g., from password reset)
  const successMessage = location.state?.message;
  const prefilledEmail = location.state?.email;
  
  // Get tier info from URL params (for paid tier signup flow)
  const selectedTier = searchParams.get('tier');
  const billingCycle = searchParams.get('billing');

  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginCredentials>({
    defaultValues: {
      email: prefilledEmail || '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginCredentials) => {
    // event.preventDefault(); // handleSubmit from react-hook-form already prevents default

    try {
      // Clear any existing client state before login
      clearClientState();
      await login(data.email, data.password);
      
      // If user came from paid tier signup, redirect to checkout
      if (selectedTier && selectedTier !== 'free') {
        navigate(`/pricing?checkout=true&tier=${selectedTier}&billing=${billingCycle || 'monthly'}`, { replace: true });
      } else {
        // Redirect to the page they were trying to visit or dashboard
        const from = location.state?.from?.pathname || '/app/dashboard';
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.error('LoginForm: Login error caught:', err);
      const errorMessage = err.response?.data?.detail || 'Login failed. Please try again.';
      
      if (errorMessage.includes('User not found') || errorMessage.includes('Invalid credentials')) {
        toast.error(`${errorMessage} Please consider signing up.`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue your journey"
    >
      <motion.form 
        onSubmit={handleSubmit(onSubmit)} 
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {/* Success Message */}
        {successMessage && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-900/20 border border-green-800 rounded-xl p-4"
          >
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <p className="text-sm text-green-300">{successMessage}</p>
            </div>
          </motion.div>
        )}

        {/* Email Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              type="email"
              placeholder="Email address"
              className="w-full pl-12 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
          </div>
          {errors.email && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.email.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Password Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="w-full pl-12 pr-12 py-4 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-300 focus:outline-none transition-colors"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.password.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          className="w-full text-white py-4 px-6 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', focusRingColor: '#30b3dd' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Signing in...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <LogIn className="w-5 h-5" />
              <span>Sign In</span>
            </div>
          )}
        </motion.button>
      </motion.form>

      {/* Divider */}
      <motion.div 
        className="mt-8 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.5 }}
      >
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-slate-900 text-slate-400">or continue with</span>
          </div>
        </div>
      </motion.div>

      {/* Google OAuth Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <GoogleOAuthButton 
          disabled={isSubmitting}
        />
      </motion.div>

      <motion.div 
        className="mt-8 text-center space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.7 }}
      >
        <p className="text-slate-400">
          Don't have an account?{' '}
          <Link
            to="/register"
            className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
          >
            Sign up
          </Link>
        </p>
        <p className="text-slate-400">
          <Link
            to="/forgot-password"
            className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Forgot your password?
          </Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
};

export default LoginForm;